import type { MeshWorkerRequest, MeshWorkerResponse } from "../workers/mesh.worker";

/** Result of parsing one OBJ tile, in scene-space float arrays. */
export interface ParsedTile {
  positions: Float32Array;
  uvs: Float32Array | null;
  normals: Float32Array | null;
  groups: Array<{ material: string; start: number; count: number }>;
  localBbox: { min: [number, number, number]; max: [number, number, number] };
  vertexCount: number;
  triangleCount: number;
  materialLibs: string[];
}

interface Pending {
  resolve: (value: ParsedTile) => void;
  reject: (error: Error) => void;
}

/**
 * Round-robin pool of OBJ parser workers.
 *
 * Kept small: parsing is memory-hungry (a 40 MB tile expands to well over 100 MB of typed
 * arrays), so running one per core would push a tab towards its heap limit.
 */
export class MeshWorkerPool {
  private readonly workers: Worker[] = [];
  private readonly pending = new Map<number, Pending>();
  private nextId = 1;
  private cursor = 0;

  constructor(size = Math.max(1, Math.min(3, Math.floor((navigator.hardwareConcurrency || 4) / 2)))) {
    for (let i = 0; i < size; i++) {
      const worker = new Worker(new URL("../workers/mesh.worker.ts", import.meta.url), { type: "module" });
      worker.addEventListener("message", (event: MessageEvent<MeshWorkerResponse>) => {
        const message = event.data;
        const entry = this.pending.get(message.id);
        if (!entry) return;
        this.pending.delete(message.id);
        if (!message.ok || !message.positions) {
          entry.reject(new Error(message.error ?? "OBJ parsing failed"));
          return;
        }
        entry.resolve({
          positions: message.positions,
          uvs: message.uvs ?? null,
          normals: message.normals ?? null,
          groups: message.groups ?? [],
          localBbox: message.localBbox ?? { min: [0, 0, 0], max: [0, 0, 0] },
          vertexCount: message.vertexCount ?? 0,
          triangleCount: message.triangleCount ?? 0,
          materialLibs: message.materialLibs ?? [],
        });
      });
      worker.addEventListener("error", (event) => {
        for (const [id, entry] of this.pending) {
          entry.reject(new Error(`OBJ worker failed: ${event.message}`));
          this.pending.delete(id);
        }
      });
      this.workers.push(worker);
    }
  }

  parse(bytes: Uint8Array, shift: [number, number, number]): Promise<ParsedTile> {
    const id = this.nextId++;
    const worker = this.workers[this.cursor++ % this.workers.length];
    // `bytes` may be a view onto a larger buffer, so copy out exactly the tile's bytes
    // before transferring ownership to the worker.
    const buffer = bytes.byteOffset === 0 && bytes.byteLength === bytes.buffer.byteLength
      ? (bytes.buffer as ArrayBuffer)
      : bytes.slice().buffer;
    return new Promise<ParsedTile>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      const request: MeshWorkerRequest = { id, buffer, shift };
      worker.postMessage(request, [buffer]);
    });
  }

  dispose(): void {
    for (const worker of this.workers) worker.terminate();
    this.workers.length = 0;
    for (const [, entry] of this.pending) entry.reject(new Error("Mesh worker pool disposed"));
    this.pending.clear();
  }
}
